import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "annotation-list-panel" }
const _hoisted_2 = { class: "panel-header" }
const _hoisted_3 = { class: "panel-title" }
const _hoisted_4 = { class: "annotation-count" }
const _hoisted_5 = { class: "panel-controls" }
const _hoisted_6 = ["placeholder"]
const _hoisted_7 = { value: "all" }
const _hoisted_8 = { value: "text" }
const _hoisted_9 = { value: "highlight" }
const _hoisted_10 = { value: "rectangle" }
const _hoisted_11 = { value: "circle" }
const _hoisted_12 = { value: "freehand" }
const _hoisted_13 = { value: "measure-distance" }
const _hoisted_14 = { value: "measure-area" }
const _hoisted_15 = { class: "annotation-items" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "annotation-content" }
const _hoisted_18 = { class: "annotation-header" }
const _hoisted_19 = { class: "annotation-type" }
const _hoisted_20 = {
  key: 0,
  class: "page-badge"
}
const _hoisted_21 = {
  key: 0,
  class: "annotation-text"
}
const _hoisted_22 = { class: "annotation-meta" }
const _hoisted_23 = { class: "annotation-time" }
const _hoisted_24 = {
  key: 0,
  class: "annotation-author"
}
const _hoisted_25 = { class: "annotation-actions" }
const _hoisted_26 = ["title", "onClick"]
const _hoisted_27 = ["title", "onClick"]
const _hoisted_28 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_29 = { class: "panel-footer" }

import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { AnnotationListItem, AnnotationTool } from '@/types/annotation';

interface Props {
  annotations: AnnotationListItem[];
  selectedAnnotationId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AnnotationListPanel',
  props: {
    annotations: {},
    selectedAnnotationId: {}
  },
  emits: ["annotation-select", "annotation-jump", "annotation-delete", "export-list"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();

const searchQuery = ref('');
const filterType = ref('all');

const filteredAnnotations = computed(() => {
  let filtered = props.annotations;

  // Filter by type
  if (filterType.value !== 'all') {
    filtered = filtered.filter((a: AnnotationListItem) => a.type === filterType.value);
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter((a: AnnotationListItem) => 
      (a.text && a.text.toLowerCase().includes(query)) ||
      a.type.toLowerCase().includes(query) ||
      (a.author && a.author.toLowerCase().includes(query))
    );
  }

  return filtered;
});

const getAnnotationIcon = (type: AnnotationTool): string => {
  const iconMap: Record<string, string> = {
    'text': 'fas fa-font',
    'highlight': 'fas fa-highlighter',
    'rectangle': 'fas fa-square',
    'circle': 'fas fa-circle',
    'freehand': 'fas fa-pen',
    'arrow': 'fas fa-arrow-right',
    'line': 'fas fa-minus',
    'measure-distance': 'fas fa-ruler',
    'measure-area': 'fas fa-draw-polygon',
  };
  return iconMap[type] || 'fas fa-pencil-alt';
};

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const formatTime = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return t('annotation.justNow');
  if (minutes < 60) return t('annotation.minutesAgo', { count: minutes });
  if (hours < 24) return t('annotation.hoursAgo', { count: hours });
  if (days < 7) return t('annotation.daysAgo', { count: days });
  
  return new Date(timestamp).toLocaleDateString();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h4", _hoisted_3, [
        _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fas fa-list" }, null, -1)),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.annotationList')), 1)
      ]),
      _createElementVNode("span", _hoisted_4, _toDisplayString(filteredAnnotations.value.length), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        type: "text",
        placeholder: _ctx.$t('annotation.searchAnnotations'),
        class: "search-input"
      }, null, 8, _hoisted_6), [
        [_vModelText, searchQuery.value]
      ]),
      _withDirectives(_createElementVNode("select", {
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filterType).value = $event)),
        class: "filter-select"
      }, [
        _createElementVNode("option", _hoisted_7, _toDisplayString(_ctx.$t('annotation.allTypes')), 1),
        _createElementVNode("option", _hoisted_8, _toDisplayString(_ctx.$t('annotation.text')), 1),
        _createElementVNode("option", _hoisted_9, _toDisplayString(_ctx.$t('annotation.highlight')), 1),
        _createElementVNode("option", _hoisted_10, _toDisplayString(_ctx.$t('annotation.rectangle')), 1),
        _createElementVNode("option", _hoisted_11, _toDisplayString(_ctx.$t('annotation.circle')), 1),
        _createElementVNode("option", _hoisted_12, _toDisplayString(_ctx.$t('annotation.freehand')), 1),
        _createElementVNode("option", _hoisted_13, _toDisplayString(_ctx.$t('annotation.measureDistance')), 1),
        _createElementVNode("option", _hoisted_14, _toDisplayString(_ctx.$t('annotation.measureArea')), 1)
      ], 512), [
        [_vModelSelect, filterType.value]
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAnnotations.value, (annotation) => {
        return (_openBlock(), _createElementBlock("div", {
          key: annotation.id,
          class: _normalizeClass(['annotation-item', { selected: _ctx.selectedAnnotationId === annotation.id }]),
          onClick: ($event: any) => (_ctx.$emit('annotation-select', annotation))
        }, [
          _createElementVNode("div", {
            class: "annotation-icon",
            style: _normalizeStyle({ color: annotation.color })
          }, [
            _createElementVNode("i", {
              class: _normalizeClass(getAnnotationIcon(annotation.type))
            }, null, 2)
          ], 4),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("div", _hoisted_18, [
              _createElementVNode("span", _hoisted_19, _toDisplayString(_ctx.$t(`annotation.${annotation.type}`)), 1),
              (annotation.pageNumber)
                ? (_openBlock(), _createElementBlock("span", _hoisted_20, " P" + _toDisplayString(annotation.pageNumber), 1))
                : _createCommentVNode("", true)
            ]),
            (annotation.text)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(truncateText(annotation.text, 50)), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_22, [
              _createElementVNode("span", _hoisted_23, _toDisplayString(formatTime(annotation.timestamp)), 1),
              (annotation.author)
                ? (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(annotation.author), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_25, [
            _createElementVNode("button", {
              class: "action-btn",
              title: _ctx.$t('annotation.jumpTo'),
              onClick: _withModifiers(($event: any) => (_ctx.$emit('annotation-jump', annotation)), ["stop"])
            }, [...(_cache[4] || (_cache[4] = [
              _createElementVNode("i", { class: "fas fa-crosshairs" }, null, -1)
            ]))], 8, _hoisted_26),
            _createElementVNode("button", {
              class: "action-btn action-btn--danger",
              title: _ctx.$t('common.delete'),
              onClick: _withModifiers(($event: any) => (_ctx.$emit('annotation-delete', annotation)), ["stop"])
            }, [...(_cache[5] || (_cache[5] = [
              _createElementVNode("i", { class: "fas fa-trash" }, null, -1)
            ]))], 8, _hoisted_27)
          ])
        ], 10, _hoisted_16))
      }), 128)),
      (filteredAnnotations.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
            _cache[6] || (_cache[6] = _createElementVNode("i", { class: "fas fa-inbox" }, null, -1)),
            _createElementVNode("p", null, _toDisplayString(searchQuery.value ? _ctx.$t('annotation.noMatchingAnnotations') : _ctx.$t('annotation.noAnnotations')), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_29, [
      _createElementVNode("button", {
        class: "footer-btn",
        onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('export-list')))
      }, [
        _cache[7] || (_cache[7] = _createElementVNode("i", { class: "fas fa-download" }, null, -1)),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.exportList')), 1)
      ])
    ])
  ]))
}
}

})